import type { FatiguePoint } from '../lib/recovery'

const W = 320
const H = 128
const PAD = { t: 12, r: 8, b: 24, l: 32 }

function formatDay(at: number): string {
  return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export function FatigueChart({
  points,
  label,
}: {
  points: FatiguePoint[]
  label: string
}) {
  if (points.length < 2) {
    return <p className="muted small">Not enough history to chart.</p>
  }

  const t0 = points[0].at
  const t1 = points[points.length - 1].at
  const span = Math.max(1, t1 - t0)
  const innerW = W - PAD.l - PAD.r
  const innerH = H - PAD.t - PAD.b

  const xy = (p: FatiguePoint) => {
    const x = PAD.l + ((p.at - t0) / span) * innerW
    const y = PAD.t + (1 - p.fatigue) * innerH
    return { x, y }
  }

  const coords = points.map(xy)
  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')
  const area = `${line} L${coords[coords.length - 1].x.toFixed(1)},${(PAD.t + innerH).toFixed(1)} L${coords[0].x.toFixed(1)},${(PAD.t + innerH).toFixed(1)} Z`
  const peak = Math.max(...points.map((p) => p.fatigue))
  const latest = points[points.length - 1].fatigue

  return (
    <figure className="fatigue-chart">
      <figcaption className="muted small">
        Fatigue · last 14 days
        {peak > 0 ? ` · peak ${Math.round(peak * 100)}%` : ''}
      </figcaption>
      <svg
        className="fatigue-chart-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${label} fatigue history. Now ${Math.round(latest * 100)} percent.`}
      >
        <line
          className="fatigue-chart-grid"
          x1={PAD.l}
          y1={PAD.t}
          x2={W - PAD.r}
          y2={PAD.t}
        />
        <line
          className="fatigue-chart-grid"
          x1={PAD.l}
          y1={PAD.t + innerH / 2}
          x2={W - PAD.r}
          y2={PAD.t + innerH / 2}
        />
        <line
          className="fatigue-chart-grid"
          x1={PAD.l}
          y1={PAD.t + innerH}
          x2={W - PAD.r}
          y2={PAD.t + innerH}
        />
        <text className="fatigue-chart-axis" x={4} y={PAD.t + 4}>
          100%
        </text>
        <text className="fatigue-chart-axis" x={4} y={PAD.t + innerH / 2 + 4}>
          50%
        </text>
        <text className="fatigue-chart-axis" x={8} y={PAD.t + innerH + 4}>
          0
        </text>
        <text className="fatigue-chart-axis" x={PAD.l} y={H - 6}>
          {formatDay(t0)}
        </text>
        <text className="fatigue-chart-axis fatigue-chart-axis-end" x={W - PAD.r} y={H - 6}>
          {formatDay(t1)}
        </text>
        <path className="fatigue-chart-area" d={area} />
        <path className="fatigue-chart-line" d={line} fill="none" />
      </svg>
    </figure>
  )
}
